'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import styles from './PrincipleBadges.module.css';

// Principle card keys that map to translation keys, with their citations
const PRINCIPLE_CARD_KEYS = [
  { key: 'fitts', citation: 'Fitts, 1954' },
  { key: 'hicks', citation: 'Hick, 1952' },
  { key: 'jakobs', citation: 'Nielsen, 2000' },
  { key: 'millers', citation: 'Miller, 1956' },
  { key: 'aesthetic', citation: 'Kurosu & Kashimura, 1995' },
  { key: 'gestalt', citation: 'Wertheimer, 1923' },
  { key: 'fpattern', citation: 'Nielsen, 2006' },
  { key: 'hierarchy', citation: 'Lidwell et al., 2003' },
  { key: 'cognitive', citation: 'Sweller, 1988' },
  { key: 'serial', citation: 'Murdock, 1962' },
  { key: 'vonrestorff', citation: 'von Restorff, 1933' },
  { key: 'zeigarnik', citation: 'Zeigarnik, 1927' },
];

// All research logos for credibility (sizes increased 20% for better visibility)
const RESEARCH_LOGOS = [
  { src: '/logos/nielsen-logo-white.svg', alt: 'Nielsen Norman Group', width: 120 },
  { src: '/logos/mit-logo-white.svg', alt: 'MIT', width: 96 },
  { src: '/logos/ibm-logo-white.svg', alt: 'IBM Research', width: 66 },
  { src: '/logos/google-ai-logo-white.svg', alt: 'Google AI', width: 108 },
  { src: '/logos/microsoft-logo-white.svg', alt: 'Microsoft Research', width: 114 },
  { src: '/logos/apple-logo-white.svg', alt: 'Apple HIG', width: 26 },
];

export function PrincipleBadges() {
  const t = useTranslations('audit.principles');

  // Duplicate cards for seamless infinite scroll
  const duplicatedCards = [...PRINCIPLE_CARD_KEYS, ...PRINCIPLE_CARD_KEYS];

  return (
    <section className={styles.section}>
      <div className="audit-container">
        {/* Header */}
        <div className={styles.header}>
          <span className={styles.label}>{t('label')}</span>
          <h2 className={styles.title}>{t('title')}</h2>
          <p className={styles.subtitle}>{t('subtitle')}</p>

          {/* Research Logos */}
          <div className={styles.researchLogos}>
            <span className={styles.logosLabel}>{t('citingFrom')}</span>
            <div className={styles.logosList}>
              {RESEARCH_LOGOS.map((logo) => (
                <Image
                  key={logo.src}
                  src={logo.src}
                  alt={logo.alt}
                  width={logo.width}
                  height={24}
                  className={styles.researchLogo}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Infinite Marquee */}
      <div className={styles.marqueeWrapper}>
        <div className={styles.marquee}>
          <div className={styles.marqueeContent}>
            {duplicatedCards.map((card, index) => (
              <div key={`${card.key}-${index}`} className={styles.principleCard}>
                <h3 className={styles.cardTitle}>{t(`cards.${card.key}.name`)}</h3>
                <p className={styles.cardDescription}>{t(`cards.${card.key}.description`)}</p>
                <span className={styles.cardCitation}>{card.citation}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Gradient overlays for smooth fade */}
        <div className={styles.gradientLeft} />
        <div className={styles.gradientRight} />
      </div>

      {/* More principles CTA */}
      <div className="audit-container">
        <div className={styles.moreSection}>
          <span className={styles.moreCount}>+156 {t('more')}</span>
          <p className={styles.moreText}>{t('moreText')}</p>
        </div>
      </div>
    </section>
  );
}
